import React, { useState, useEffect } from 'react';
import { FileText, Tag, Calendar, ChevronDown, ChevronUp, ZoomIn, X } from 'lucide-react';
import api from '../../services/apiService';

const ChangesList = () => {
  const [changes, setChanges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [imageModal, setImageModal] = useState({ open: false, src: '', title: '' });
  const [selectedCategory, setSelectedCategory] = useState('');

  const categoryOptions = [
    { value: '', label: 'All types' },
    { value: 'feature', label: 'New Feature' },
    { value: 'improvement', label: 'Improvement' },
    { value: 'bugfix', label: 'Bug Fix' },
    { value: 'other', label: 'Other' },
  ];

  const fetchChanges = async (pageNum = 1, append = false, category = selectedCategory) => {
    try {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      let url = `/changelogs/?page=${pageNum}`;
      if (category) {
        url += `&category=${category}`;
      }
      const response = await api.get(url);
      const data = response.data;

      if (append) {
        setChanges(prev => [...prev, ...data.results]);
      } else {
        setChanges(data.results || []);
      }

      setTotalCount(data.count || 0);
      setHasMore(!!data.next);
      setPage(pageNum);
    } catch (err) {
      console.error('Error fetching changelogs:', err);
      setError('Failed to load changes');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchChanges(1, false, selectedCategory);
  }, [selectedCategory]);

  const handleCategoryChange = (e) => {
    setSelectedCategory(e.target.value);
    setPage(1);
  };

  const handleLoadMore = () => {
    fetchChanges(page + 1, true);
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'feature':
        return 'bg-green-100 text-green-800';
      case 'improvement':
        return 'bg-blue-100 text-blue-800';
      case 'bugfix':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isNewEntry = (dateString) => {
    const entryDate = new Date(dateString);
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    return entryDate > weekAgo;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-red-600">{error}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Image Modal */}
      {imageModal.open && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4"
          onClick={() => setImageModal({ open: false, src: '', title: '' })}
        >
          <div className="relative max-w-5xl max-h-full">
            <button
              onClick={() => setImageModal({ open: false, src: '', title: '' })}
              className="absolute -top-10 right-0 text-white hover:text-gray-300 transition-colors"
            >
              <X className="h-8 w-8" />
            </button>
            <img
              src={imageModal.src}
              alt={imageModal.title}
              className="max-w-full max-h-[85vh] rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}

      <div className="max-w-6xl mx-auto">
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              <FileText className="h-6 w-6 mr-2 text-blue-600" />
              Changes List
            </h1>
            <select
              value={selectedCategory}
              onChange={handleCategoryChange}
              className="min-w-[160px] px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {categoryOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <p className="text-gray-600 mt-1">Recent updates and improvements</p>
        </div>

        {changes.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
            No changes to display
          </div>
        ) : (
          <div className="space-y-4">
            {changes.map((change) => (
              <div
                key={change.id}
                className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden"
              >
                <div
                  className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => setExpandedId(expandedId === change.id ? null : change.id)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        {isNewEntry(change.created_at) && (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            NEW
                          </span>
                        )}
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(change.category)}`}>
                          {change.category_display}
                        </span>
                        <span className="text-xs text-gray-500 flex items-center">
                          <Calendar className="h-3 w-3 mr-1" />
                          {formatDate(change.created_at)}
                        </span>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900">{change.title}</h3>
                    </div>
                    <button className="ml-4 text-gray-400">
                      {expandedId === change.id ? (
                        <ChevronUp className="h-5 w-5" />
                      ) : (
                        <ChevronDown className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </div>

                {expandedId === change.id && (
                  <div className="px-4 pb-4 border-t border-gray-100">
                    <div className="pt-4">
                      <div className="flex items-start justify-between">
                        <p className="text-gray-700 whitespace-pre-wrap flex-1">{change.description}</p>
                        {change.image && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setImageModal({ open: true, src: change.image, title: change.title });
                            }}
                            className="ml-4 p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="View image"
                          >
                            <ZoomIn className="h-5 w-5" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}

            {hasMore && (
              <div className="text-center pt-4">
                <button
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loadingMore ? (
                    <span className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Loading...
                    </span>
                  ) : (
                    `Show more (${changes.length} of ${totalCount})`
                  )}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChangesList;
